'use client'

import { type FC } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'

import { IconPlus } from '@redteclab/icons'

import { Skeleton } from 'base-ui'

import {
  CROSS_SELL_ANALYTICS_DATA_NAME,
  CROSS_SELL_PDP_BUY_TOGETHER_WITH,
  crossSellAccessorGetLocalizedProductDeepLink,
  crossSellAccessorGetLocalizedProductName,
  useCrossSellConfigContext,
  useCrossSellWidgetTitle,
  useSwrCrossSellHorizontalSlider,
} from '../../../../cross-sell'
import { useGlobalConfigContext } from '../../../../global-config'
import {
  gtmDataLayerPushCswProduct,
  gtmDataLayerPushHomeOneAddToCart,
} from '../../../../gtm'
import { productAccessorGetRedPointsRedPointsAmountWithMultiplier } from '../../../model/productAccessor/productAccessorGetRedPointsRedPointsAmountWithMultiplier'
import { useProductPageContext } from '../../productPageContext'
import { useCrossSellListPriceQueryParam } from '../hooks/useCrossSellListPriceQueryParam'
import { useCrossSellRetailPriceQueryParam } from '../hooks/useCrossSellRetailPriceQueryParam'

import { ProductCrossSellBuyTogetherAddToCart } from './ProductCrossSellBuyTogetherAddToCart'
import { ProductCrossSellBuyTogetherPrices } from './ProductCrossSellBuyTogetherPrices'
import { ProductCrossSellBuyTogetherTitle } from './ProductCrossSellBuyTogetherTitle'
import { ProductCrossSellProductThumbnail } from './ProductCrossSellProductThumbnail'

// eslint-disable-next-line max-lines-per-function -- the rule was disabled before a comment became required
export const ProductCrossSellBuyTogether: FC = () => {
  const { language, tenant } = useGlobalConfigContext()
  const { currentOffer, currentVariant } = useProductPageContext()
  const { widgets } = useCrossSellConfigContext()

  const widgetId = widgets[CROSS_SELL_PDP_BUY_TOGETHER_WITH]?.id

  const { isIntersecting, ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    rootMargin: '0px 0px 200px',
  })

  const listPrice = useCrossSellListPriceQueryParam()

  const retailPrice = useCrossSellRetailPriceQueryParam()

  const { data, error } = useSwrCrossSellHorizontalSlider(
    isIntersecting && widgetId
      ? {
          listPrice,
          productId: currentVariant.upid,
          retailPrice,
          widgetId,
        }
      : undefined,
  )
  const widgetTitle = useCrossSellWidgetTitle({
    data,
    widgetType: CROSS_SELL_PDP_BUY_TOGETHER_WITH,
  })

  if (!widgetId || data?.items.length === 0 || !currentOffer || error) {
    return null
  }

  const [recommendedProduct] = data?.items ?? []

  return (
    <div
      className="cross-sell-buytogether-widget flex flex-col"
      data-analytics={CROSS_SELL_ANALYTICS_DATA_NAME}
      data-crosssell-id={widgetId}
      data-crosssell-title={widgetTitle ?? ''}
      data-qa-widget-type={CROSS_SELL_PDP_BUY_TOGETHER_WITH}
      ref={ref}
    >
      <ProductCrossSellBuyTogetherTitle>
        {widgetTitle}
      </ProductCrossSellBuyTogetherTitle>
      <div className="relative mb-7 flex items-center rounded-2xl border border-dark-primary-min p-3.5 after:absolute after:bottom-[-21px] after:left-1/2 after:size-0 after:-translate-x-1/2 after:border after:border-x-[21px] after:border-t-[21px] after:border-transparent after:border-t-[var(--background-light-primary-medium)]">
        {data ? (
          <ProductCrossSellProductThumbnail
            className="flex-1 self-start"
            imageUrl={currentVariant.images?.[0]}
            productId={currentVariant.upid}
            productName={currentVariant.name}
            productUrl={currentVariant.canonical_url}
            redpoints={productAccessorGetRedPointsRedPointsAmountWithMultiplier(
              currentOffer,
            )}
          />
        ) : (
          <Skeleton className="h-56 w-40" variant="rectangular" />
        )}
        <div className="flex flex-1 justify-center">
          <IconPlus className="size-8 fill-dark-brand" />
        </div>
        {data ? (
          <ProductCrossSellProductThumbnail
            className="flex-1 self-start"
            imageAsUrl
            imageUrl={recommendedProduct.iconurl}
            onGtmDataLayerPush={() => {
              gtmDataLayerPushCswProduct({
                cswProductId: recommendedProduct.id,
                productId: currentVariant.upid,
                widgetId,
                widgetTitle,
              })
            }}
            productId={recommendedProduct.id}
            productName={crossSellAccessorGetLocalizedProductName({
              language,
              product: recommendedProduct,
              tenant,
            })}
            productUrl={
              crossSellAccessorGetLocalizedProductDeepLink({
                language,
                product: recommendedProduct,
                tenant,
              }) || data.widgetdetails.deeplinkfallbackurl
            }
          />
        ) : (
          <Skeleton className="h-56 w-40" variant="rectangular" />
        )}
      </div>
      <div className="flex justify-center">
        {data ? (
          <ProductCrossSellBuyTogetherPrices
            recommendedProduct={recommendedProduct}
          />
        ) : (
          <Skeleton className="h-20 w-40" variant="rectangular" />
        )}
      </div>
      {data ? (
        <ProductCrossSellBuyTogetherAddToCart
          currentOffer={currentOffer}
          currentVariant={currentVariant}
          onGtmDataLayerPush={() => {
            gtmDataLayerPushHomeOneAddToCart()
            gtmDataLayerPushCswProduct({
              cswProductId: recommendedProduct.id,
              productId: currentVariant.upid,
              widgetId,
              widgetTitle,
            })
          }}
          recommendedProduct={recommendedProduct}
        />
      ) : (
        <Skeleton className="mt-7 h-12" variant="rectangular" />
      )}
    </div>
  )
}
